import Dexie from 'dexie';
import type { Table } from 'dexie';
import { config } from '../config';
import { SchemaVersionError, StorageError } from '../app/errors';
import type {
  ActiveMatchRow,
  AnnotationRow,
  CoachCardRow,
  FlagResolutionRow,
  HypothesisRow,
  LogEntryRow,
  MatchNoteRow,
  MatchRow,
  ObservationRow,
  OurPlayerRow,
  OutboxKind,
  OutboxRow,
  PlayerRow,
  SetPieceRow,
  SettingRow,
  TalkPointRow,
  TeamRow,
  TemplateRow,
  UiSessionRow,
} from '../types';
import { applyProductMigrations, LATEST_PRODUCT_VERSION } from './migrations/product';
import { bundleNativeVersion, storedVersionOf } from './recoveryDb';
import { runRead, runWrite } from './commit';
import type { RunWriteOptions, WriteResult } from './commit';

/**
 * **The product database.** Design §1.2 — `data/`: the only place `dexie` is imported.
 *
 * Separate from `harnessDb.ts`, which carries build units U-0 and U-1. NFR-7 wants the product's
 * first shipped schema to be *its own* `v001`; the harness had already spent v001 and v002 on
 * measurement stores that will be deleted when A-1 and A-2 are settled.
 *
 * ── The one thing to read before writing anything through this module ────────
 *
 * `write()` is the only write path, and it resolves **after the native `IDBTransaction` `complete`
 * event**, never on Dexie's transaction promise. See `commit.ts` for why those are different
 * moments in different orders in a browser and under `fake-indexeddb`, and why SES-2 depends on the
 * difference. Nothing here exports a way to get the Dexie transaction promise, and a lint rule
 * refuses a bare `.transaction(` outside `data/` — so the wrong thing is not reachable.
 */

/**
 * The audio row. **Declared here rather than in `types/` — design §3.1's boundary rule.**
 *
 * `AudioBlob.blob` is the only lib-typed field in the whole schema. `types/` is compiled without DOM
 * libs (because `domain/` imports it and NFR-6 makes `domain/` DOM-free), so a `Blob` there would
 * either fail the build or force `types/` to be widened — and widening is the plausible wrong answer
 * that makes the error disappear while disabling the boundary. `data/` already depends on Dexie and
 * the DOM, so this is the correct home. Nothing in `domain/` references it: `replay` and `reconcile`
 * take `Observation[]`, and an `Observation` carries `audioBlobId: string`, never the bytes.
 */
export interface AudioBlobRow {
  id: string;
  /**
   * The assembled recording, verbatim (AUD-1 forbids transcoding on any path).
   * `null` until finalize runs — the Observation is written at recording **start** (§8.2), so a row
   * exists before any bytes do.
   */
  blob: Blob | null;
  /**
   * Chunks as they arrived, held until finalize assembles them (§8.2 step 2, §8.5).
   *
   * §8.2 step 3 says finalize "assembles chunks verbatim in arrival order" — so they are stored *as
   * chunks* until then. Keeping them as an array rather than re-concatenating the whole blob on
   * every `dataavailable` makes each append O(1) in bytes instead of O(n), which matters because a
   * `VOICE_NOTE_MAX_S` note produces ~60 of them, and it gives §8.5's `pageshow` recovery pass the
   * arrival order explicitly rather than inferring it from a single opaque blob.
   */
  chunks: Blob[];
  /** `recorder.mimeType` — AUD-1 stores what was emitted, never a guess. Empty until known. */
  mime: string;
  /** Running byte total across `chunks`, or the assembled size once complete. */
  bytes: number;
  /**
   * False until finalize ran. **Never indexed** — IndexedDB does not accept a boolean as a key, so
   * the index would be declared, accepted, and hold no entries (found by U-1 on `audioSegments`).
   */
  complete: boolean;
  createdAt: number;
}

/**
 * Table names, in one place.
 *
 * A transaction scope is a list of table-name strings; a typo produces a runtime `NotFoundError`
 * inside a transaction rather than a compile error. Naming them once makes the scope lists checkable
 * and makes "which tables does the cascade span?" answerable by reading one object.
 */
export const TABLE = {
  teams: 'teams',
  players: 'players',
  templates: 'templates',
  settings: 'settings',
  matches: 'matches',
  hypotheses: 'hypotheses',
  observations: 'observations',
  flagResolutions: 'flagResolutions',
  setPieces: 'setPieces',
  talkPoints: 'talkPoints',
  matchNotes: 'matchNotes',
  // B-1 (spec §4.1, migration v004): match-scoped B rows, then the profile-scoped roster below.
  annotations: 'annotations',
  coachCards: 'coachCards',
  ourPlayers: 'ourPlayers',
  audioBlobs: 'audioBlobs',
  outbox: 'outbox',
  uiSession: 'uiSession',
  activeMatch: 'activeMatch',
  logEntries: 'logEntries',
} as const;

export type TableName = (typeof TABLE)[keyof typeof TABLE];

/**
 * The tables INV-8's cascade spans, and the tables SYN-4's per-match archive covers.
 *
 * Declared once because both readers must agree: a table missing here leaves orphan rows on delete
 * *and* drops them from the archive, and those two bugs look nothing alike in the field.
 */
export const MATCH_SCOPED_TABLES: readonly TableName[] = [
  TABLE.hypotheses,
  TABLE.observations,
  TABLE.flagResolutions,
  TABLE.setPieces,
  TABLE.talkPoints,
  TABLE.matchNotes,
  // B-1 (spec §4.1): both new match-scoped tables join the INV-8 cascade and the per-match
  // archive scope through this one list. `ourPlayers` is deliberately NOT here — the roster is
  // profile-scoped and survives every match delete (the B-1 cascade-negative test pins this).
  TABLE.annotations,
  TABLE.coachCards,
];

export class TouchlineDb extends Dexie {
  declare teams: Table<TeamRow, string>;
  declare players: Table<PlayerRow, string>;
  declare templates: Table<TemplateRow, string>;
  declare settings: Table<SettingRow, string>;
  declare matches: Table<MatchRow, string>;
  declare hypotheses: Table<HypothesisRow, string>;
  declare observations: Table<ObservationRow, string>;
  declare flagResolutions: Table<FlagResolutionRow, string>;
  declare setPieces: Table<SetPieceRow, string>;
  declare talkPoints: Table<TalkPointRow, string>;
  declare matchNotes: Table<MatchNoteRow, string>;
  declare annotations: Table<AnnotationRow, string>;
  declare coachCards: Table<CoachCardRow, string>;
  declare ourPlayers: Table<OurPlayerRow, string>;
  declare audioBlobs: Table<AudioBlobRow, string>;
  /** Compound primary key `[kind+scopeId]` — see the note on `OutboxRow`. */
  declare outbox: Table<OutboxRow, [OutboxKind, string]>;
  declare uiSession: Table<UiSessionRow, string>;
  declare activeMatch: Table<ActiveMatchRow, string>;
  declare logEntries: Table<LogEntryRow, number>;

  constructor(name: string = config.PRODUCT_DB_NAME) {
    super(name);
    applyProductMigrations(this);
    guardAgainstNewerSchema(this);
  }
}

/**
 * **NFR-7 / §13.5 — refuse a database this bundle is too old to understand, at the open.**
 *
 * §13.5's recovery screen keys on `SchemaVersionError`, and `openDb()` below has raised one from
 * `Dexie.VersionError` since U-6. Build unit B-21 measured what actually happens and found that
 * branch **unreachable**: Dexie 4.4.4 contains no `throw` of `VersionError` anywhere — only a
 * `catch` (`dexie.js` ~line 4599) that, on IndexedDB refusing `open(name, 40)` against a stored
 * native version of 50, **retries the open with no version at all** and succeeds. Measured
 * consequences, both in `recoveryDb.test.ts`:
 *
 * | What the older bundle then does | Why it is worse than a crash |
 * |---|---|
 * | opens, reporting `verno` as its own declared version | the app believes it is current |
 * | sees only the stores **it** declares | a newer table is invisible; its rows are not read, not exported, not counted |
 * | writes through a field list missing the newer schema's fields | a `put` of a row it read drops what it could not see — silent truncation of the user's data |
 *
 * So the condition the screen exists for produced no error, no screen, and a quietly half-blind app.
 * This handler is the mechanism that turns it back into the named condition §13.5 describes.
 *
 * ── Why `on('ready')` and not a check after `openDb()` ───────────────────────
 *
 * `openDb()` has no production caller today; storage is reached through `db()`, which lets Dexie
 * **auto-open** on the first table operation. A check that lived only in `openDb()` would therefore
 * be racing `bootstrapApp()`'s seed install — a *write* — for which of them reached the newer
 * database first. Dexie holds every queued operation until its `ready` subscribers have run, and a
 * subscriber that throws rejects the open and closes the connection, so putting it here means **no
 * transaction of any kind can run against a newer database**, whatever opened it and in whatever
 * order. That is what makes "the recovery screen destroys nothing" a mechanism rather than a claim,
 * and it is why `main-b.tsx` did not have to be resequenced (its listener registration before the
 * first `await` is load-bearing for the bfcache `pageshow` — see `app/bootstrap.ts`).
 *
 * **The normal path is one integer comparison.** Stored equal to declared — every launch that is not
 * this failure — compares equal and returns, and nothing else in this module changes.
 */
function guardAgainstNewerSchema(handle: TouchlineDb): void {
  handle.on('ready', () => {
    const stored = handle.backendDB().version;
    const bundle = bundleNativeVersion();
    if (stored <= bundle) return;
    throw new SchemaVersionError(
      'This copy of Touchline is older than the data on this device. Update the app; your matches are safe and can be exported from the recovery screen.',
      {
        context: {
          name: handle.name,
          bundleVersion: LATEST_PRODUCT_VERSION,
          storedVersion: storedVersionOf(stored),
          storedNativeVersion: stored,
        },
      },
    );
  });
}

let instance: TouchlineDb | null = null;

/** The process-wide connection. `data/` owns its lifecycle; nothing else constructs a `Dexie`. */
export function db(): TouchlineDb {
  instance ??= new TouchlineDb();
  return instance;
}

/**
 * Opens the connection. **B-21 made this the boot probe** — `ui-b/AppB.tsx` awaits it before it will
 * render a shell, so the §13.5 condition is decided once, in front of the user, rather than
 * surfacing later as whichever read happened to run first.
 *
 * Three throws, and the difference between the first two is the whole point of §13.5:
 *
 * @throws {SchemaVersionError} when the stored schema is newer than this bundle. Raised by
 *   {@link guardAgainstNewerSchema} at the `ready` event and **re-thrown here unchanged** — wrapping
 *   it as a generic open failure is exactly the conflation the class exists to prevent, and it would
 *   put a data-loss path behind a message that reads like a transient error.
 * @throws {SchemaVersionError} (second source, retained) when Dexie itself reports a
 *   `VersionError`. Measured not to happen in 4.4.4 — see {@link guardAgainstNewerSchema} — but this
 *   is a translation of somebody else's error class, and deleting a check because the current
 *   version of a dependency does not currently reach it is how the next version gets a crash instead
 *   of a screen.
 * @throws {StorageError} on any other open failure. Surfaced, never swallowed (NFR-8).
 */
export async function openDb(): Promise<TouchlineDb> {
  const handle = db();
  try {
    await handle.open();
  } catch (cause) {
    if (cause instanceof SchemaVersionError) throw cause;
    if (cause instanceof Dexie.VersionError) {
      throw new SchemaVersionError(
        'This copy of Touchline is older than the data on this device. Update the app; your matches are safe and can be exported from the recovery screen.',
        { cause, context: { name: handle.name, bundleVersion: LATEST_PRODUCT_VERSION } },
      );
    }
    throw new StorageError('Could not open the Touchline database.', {
      cause,
      context: { name: handle.name, bundleVersion: LATEST_PRODUCT_VERSION },
    });
  }
  return handle;
}

/**
 * Design §5.1 "Protections" — the connection is warmed with a no-op `readwrite` on entering an
 * in-match state and on `visibilitychange → visible`, so the first tap of a half does not pay the
 * connection's cold cost inside NFR-1's budget.
 *
 * Deliberately **not** routed through `write()`: it commits nothing a user could be told about, so
 * there is no acknowledgment to gate and no reason to wait for a commit event.
 *
 * @throws {StorageError}
 */
export async function warmConnection(): Promise<void> {
  const handle = db();
  try {
    await handle.transaction('rw', [TABLE.observations, TABLE.hypotheses], async () => {
      await handle.hypotheses.limit(1).toArray();
    });
  } catch (cause) {
    throw new StorageError('Connection warm-up transaction failed.', { cause });
  }
}

/**
 * **The write path.** One `readwrite` transaction, resolving only after the commit event (SES-2).
 *
 * @param tables the transaction scope. An operation on a table outside it throws rather than
 *   silently opening a second transaction — which is what makes "exactly one transaction per
 *   Command" (§1.1) checkable rather than aspirational.
 * @param work   the transaction body. May `await` **only** IndexedDB operations: an IndexedDB
 *   transaction dies on any non-IDB await (§8.2).
 * @param options `onCommitted` fires synchronously inside the `complete` handler — design §5.1
 *   step 9's acknowledgment point, and the only one.
 */
export function write<T>(
  tables: readonly TableName[],
  work: () => Promise<T>,
  options?: RunWriteOptions,
): Promise<WriteResult<T>> {
  return runWrite(db(), tables, work, options);
}

/** One `readonly` transaction — a consistent multi-table snapshot. Nothing to acknowledge. */
export function read<T>(
  tables: readonly TableName[],
  work: () => Promise<T>,
  label?: string,
): Promise<T> {
  return runRead(db(), tables, work, label);
}

/**
 * Closes and forgets the connection.
 *
 * Used by the writer-lock read-only path (§13.7) and by tests. Not a delete: a closed connection
 * loses nothing.
 */
export function closeDb(): void {
  instance?.close();
}

/** Test seam only — lets a test point the schema at an isolated database name. */
export function __setDbForTests(next: TouchlineDb | null): void {
  instance = next;
}
